import { Grid, MenuItem, Paper } from "@mui/material";
import { CreateHealthRecordBodyHealthRecordGenderEnum } from "@syadem/kairos-pro-js";
import { RefObject } from "react";
import { CapitalizeFirstNames } from "../../../../utils/name";
import { LightStyledInput } from "../../../components/mui/LightStyledInput";
import { LightStyledSelect } from "../../../components/mui/LightStyledSelect";
import { useI18n } from "../../../hooks/useI18n";
import { sectionPaperSx } from "./styles";
import { NewHealthRecordDraft } from "./types";

export function NewHealthRecordDraftForm({
  draft,
  setFirstName,
  setLastName,
  setGender,
  setZipCode,
  needsZipCode,
  zipCodePlaceholder,
  validateZipCode,
  invalidGender,
  setInvalidGender,
  invalidZipCode,
  setInvalidZipCode,
  genderRef,
  zipCodeRef,
}: {
  draft: NewHealthRecordDraft;
  setFirstName: (value: string) => void;
  setLastName: (value: string) => void;
  setGender: (value: CreateHealthRecordBodyHealthRecordGenderEnum) => void;
  setZipCode: (value: string) => void;
  needsZipCode: boolean;
  zipCodePlaceholder: string;
  validateZipCode: (value: string) => boolean;
  invalidGender: boolean;
  setInvalidGender: (invalid: boolean) => void;
  invalidZipCode: boolean;
  setInvalidZipCode: (invalid: boolean) => void;
  genderRef: RefObject<HTMLDivElement | null>;
  zipCodeRef: RefObject<HTMLDivElement | null>;
}) {
  const { t } = useI18n();

  return (
    <Paper sx={sectionPaperSx} elevation={0}>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <LightStyledInput
            fullWidth
            required
            label={t("common.user_infos.first_names")}
            value={draft.firstName}
            onChange={(event) => setFirstName(CapitalizeFirstNames(event.target.value))}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LightStyledInput
            fullWidth
            required
            label={t("common.user_infos.last_name")}
            value={draft.lastName}
            onChange={(event) => setLastName(event.target.value.toUpperCase())}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <LightStyledInput
            fullWidth
            required
            readOnly
            label={t("common.user_infos.birth_date")}
            value={draft.birthDate?.format("YYYY-MM-DD") ?? ""}
          />
        </Grid>
        <Grid ref={genderRef} size={{ xs: 12, md: 6 }}>
          <LightStyledSelect
            label={t("common.gender.s")}
            placeholder={`ex : ${t("common.gender.m")}`}
            renderValue={(selectedGender) => t(`common.gender.${selectedGender.toLowerCase()}`)}
            fullWidth
            required
            name="gender"
            value={draft.gender}
            error={invalidGender}
            errorMessage={invalidGender ? t("evc_onboarding.gender_required") : undefined}
            onChange={(event) => {
              setGender(event.target.value as CreateHealthRecordBodyHealthRecordGenderEnum);
              setInvalidGender(false);
            }}
          >
            <MenuItem value="M">{t("common.gender.m")}</MenuItem>
            <MenuItem value="F">{t("common.gender.f")}</MenuItem>
          </LightStyledSelect>
        </Grid>
        {needsZipCode && (
          <Grid ref={zipCodeRef} size={{ xs: 12, md: 6 }}>
            <LightStyledInput
              name="zipCode"
              fullWidth
              label={t("common.user_infos.zip_code")}
              required
              placeholder={zipCodePlaceholder}
              value={draft.zipCode}
              onChange={(event) => {
                setZipCode(event.target.value);
                setInvalidZipCode(false);
              }}
              onBlur={(event) => setInvalidZipCode(!validateZipCode(event.target.value))}
              error={invalidZipCode}
              errorMessage={invalidZipCode ? t("common.errors.zipCode") : undefined}
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
}
